/* eslint-disable no-console */
import {Col, Form, Radio, Select} from 'antd';
import useFormInstance from 'antd/es/form/hooks/useFormInstance';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import {ReactComponent as YearCalender} from '../../assets/yearCalender.svg';
import {CommonErrors, TF} from '../../constants/Common';
// import {EfdbName} from '../../constants/efdbNameConstants';
import {EfdbName} from '../../constants/efdbNameConstants';
import InformativeText from '../../constants/InformativeText';
import {ISelect} from '../../interface/ISelect';
import NumberInput from '../../shared/NumberInput';
import useCompanyStore from '../../store/companyStore/companyDetailsStore';
import useJSONStore from '../../store/StaticJson/jsonStore';
import RegEx from '../../utils/FormUtils/RegEx';
import {yearSelect} from '../../utils/FormUtils/yearSelect';
import AlertDescription from '../AlertDescription/AlertDescription';
import FormListComp from '../DrawerForm/Utils/FormListComp';
import {MYConfig, MYData} from '../MonthlyYearly/Interface/common';
import MonthlyYearlyDrawerForm from '../MonthlyYearly/MonthlyYearlyDrawerForm';
import {productType} from '../Table/Constants/Common';

import {yearKeyLabel} from './Constants/IP/commonKeyLabel';
import {
  isSacProduct,
  ProductInfoKey,
  ProductInfoLabel
} from './Constants/Products/ProductInfoKeyLabel';
import {unitType, unitTypes} from './interface/unitChange';
import changeUnit from './Utils/changeUnit';
import {filterSelectOptions} from './Utils/commonFunctions';
import ProductFacility from './Utils/TableListForm/ProductFacilityDrawer';
import ProductInfoRaw from './Utils/TableListForm/ProductInfoRawDrawer';

const getAvailableProductTypes = (
  productId: unknown,
  productsList: ISelect[]
) => {
  if (isSacProduct(productId, productsList)) {
    return productType.filter((pt) => pt.value === 'b2c');
  }

  return productType;
};

const ProductInfoFormDrawerForm: React.FC<{
  data?: any;
  setSubmitDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  currentStep?: number;
  fullDrawerSteps?: string[];
  onDrawerStepsChange?: (steps: string[]) => void;
  onSacFlowChange?: (isSac: boolean) => void;
}> = ({
  data = undefined,
  currentStep,
  fullDrawerSteps,
  onDrawerStepsChange,
  onSacFlowChange
}) => {
  const products = useJSONStore.getState().products;
  const {currency} = useCompanyStore.getState().preUnits;

  const productInfoTooltip = InformativeText?.Product;
  const yearFilter = yearSelect();
  const [searchParam] = useSearchParams();

  const {getFieldValue, setFieldValue} = useFormInstance();

  const [productUnit, setProductUnit] = useState<unitType>(
    products.find((item) => {
      return item?.value === getFieldValue([ProductInfoKey.PRODUCT_NAME]);
    })?.state ?? unitTypes.NA
  );

  const prevIsSacRef = useRef<boolean | null>(null);
  const lastProductIdRef = useRef<unknown>();

  const updateDrawerSteps = useCallback(
    (stepCount: 2 | 3) => {
      if (fullDrawerSteps?.length !== 3) {
        return;
      }

      const nextSteps =
        stepCount === 2 ? fullDrawerSteps.slice(0, 2) : fullDrawerSteps;
      onDrawerStepsChange?.(nextSteps);
    },
    [fullDrawerSteps, onDrawerStepsChange]
  );

  const syncSacProductState = useCallback(
    (productId: unknown) => {
      if (fullDrawerSteps?.length !== 3) {
        return;
      }

      const effectiveProductId =
        productId ??
        lastProductIdRef.current ??
        data?.[ProductInfoKey.PRODUCT_NAME];

      if (effectiveProductId == null || effectiveProductId === '') {
        return;
      }

      const isSac = isSacProduct(effectiveProductId, products);

      if (
        prevIsSacRef.current === isSac &&
        lastProductIdRef.current === effectiveProductId
      ) {
        return;
      }

      const wasSac = prevIsSacRef.current;
      lastProductIdRef.current = effectiveProductId;

      if (isSac) {
        updateDrawerSteps(2);
        if (getFieldValue(ProductInfoKey.PRODUCT_TYPE) !== productType[0].value) {
          setFieldValue(ProductInfoKey.PRODUCT_TYPE, productType[0].value);
        }
        if (wasSac !== true) {
          setFieldValue(ProductInfoKey.RAW_MATERIAL_DATA, []);
          setFieldValue(TF, []);
        }
      } else {
        updateDrawerSteps(3);

        if (wasSac === true) {
          setFieldValue(ProductInfoKey.RAW_MATERIAL_DATA, [{}]);
        }
      }

      if (wasSac !== isSac) {
        onSacFlowChange?.(isSac);
      }

      prevIsSacRef.current = isSac;
    },
    [data, fullDrawerSteps, onSacFlowChange, products, updateDrawerSteps]
  );

  const watchedProductId = Form.useWatch(ProductInfoKey.PRODUCT_NAME);

  useEffect(() => {
    prevIsSacRef.current = null;
    lastProductIdRef.current = undefined;
  }, [data?._id]);

  useEffect(() => {
    const productId = watchedProductId ?? data?.[ProductInfoKey.PRODUCT_NAME];
    syncSacProductState(productId);
  }, [watchedProductId, data?.[ProductInfoKey.PRODUCT_NAME], syncSacProductState]);

  const monYear: MYConfig[] = [
    {
      mainTitle: {
        key: ProductInfoKey.UNIT_PRODUCED_TF,
        title: ProductInfoLabel.UNIT_PRODUCED_TF
      },
      unit: 'No.'
    },
    {
      mainTitle: {
        key: ProductInfoKey.COST_TF,
        title: ProductInfoLabel.COST_TF,
        tooltip: productInfoTooltip?.cost_to_production
      },
      unit: currency
    },
    {
      mainTitle: {
        key: ProductInfoKey.SELLING_TF,
        title: ProductInfoLabel.SELLING_TF,
        tooltip: productInfoTooltip?.selling_price
      },
      unit: currency
    }
  ];

  return (
    <>
      {currentStep === 0 && (
        <>
          <Col className="gutter-row">
            <Form.Item
              name={yearKeyLabel.key}
              label={yearKeyLabel.label}
              labelCol={{span: 24}}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(yearKeyLabel.label)
                }
              ]}
              initialValue={searchParam.get('selectedYear')}
            >
              <Select
                placeholder={yearKeyLabel.label}
                options={yearFilter}
                suffixIcon={<YearCalender />}
                onChange={() => {
                  setFieldValue(ProductInfoKey.FACILITIES, [{}]);
                }}
                disabled={data !== undefined}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item
              name={ProductInfoKey.PRODUCT_NAME}
              rules={[
                {
                  required: true,
                  message: CommonErrors.mandatory(ProductInfoLabel.PRODUCT_NAME)
                }
              ]}
              label={ProductInfoLabel.PRODUCT_NAME}
              initialValue={undefined}
              labelCol={{span: 24}}
              validateFirst
              hasFeedback
            >
              <Select
                showSearch
                className="input"
                placeholder={`Enter ${ProductInfoLabel.PRODUCT_NAME}`}
                filterOption={filterSelectOptions}
                options={products.filter(
                  (item: any) => item?.dbName === EfdbName.eeio
                )}
                disabled={data !== undefined}
                onChange={(_, option) => {
                  setProductUnit(option?.state);
                  syncSacProductState(option?.value);
                }}
              />
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item dependencies={[ProductInfoKey.PRODUCT_NAME]} noStyle>
              {({getFieldValue}) => {
                return (
                  (products.find(
                    (formatItem: any) =>
                      formatItem.value ===
                      getFieldValue([ProductInfoKey.PRODUCT_NAME])
                  )?.state ?? unitTypes.NA) !== unitTypes.NA && (
                    <Form.Item
                      name={ProductInfoKey.VOLUME}
                      label={ProductInfoLabel.VOLUME}
                      rules={[
                        {
                          pattern: RegEx.twoDigitDecimal,
                          message: CommonErrors.twoDigitPositive
                        }
                      ]}
                      labelCol={{span: 24}}
                      validateFirst
                      hasFeedback
                    >
                      <NumberInput
                        addonAfter={changeUnit(productUnit)}
                        placeholder={`Enter ${ProductInfoLabel.WEIGHT}`}
                        isFormatted={true}
                      />
                    </Form.Item>
                  )
                );
              }}
            </Form.Item>
          </Col>
          <Col className="gutter-row">
            <Form.Item dependencies={[ProductInfoKey.PRODUCT_NAME]} noStyle>
              {({getFieldValue: getProductFieldValue}) => {
                const availableProductTypes = getAvailableProductTypes(
                  getProductFieldValue(ProductInfoKey.PRODUCT_NAME),
                  products
                );

                return (
                  <Form.Item
                    name={ProductInfoKey.PRODUCT_TYPE}
                    label={ProductInfoLabel.PRODUCT_TYPE}
                    rules={[]}
                    labelCol={{span: 24}}
                    initialValue={productType?.[0].value}
                  >
                    <Radio.Group
                      buttonStyle="solid"
                      className="toggle"
                      style={{width: '202px'}}
                    >
                      {availableProductTypes.map(({value, label}, id) => {
                        return (
                          <Radio.Button key={id} value={value}>
                            {label}
                          </Radio.Button>
                        );
                      })}
                    </Radio.Group>
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
          <Form.Item dependencies={[ProductInfoKey.PRODUCT_NAME]} noStyle>
            {({getFieldValue: getProductNameFieldValue}) => {
              const productId = getProductNameFieldValue(
                ProductInfoKey.PRODUCT_NAME
              );

              if (isSacProduct(productId, products)) {
                return null;
              }

              return (
                <>
                  <div
                    className="stepTitle"
                    style={{margin: '2rem 0 0 0', paddingBottom: 0}}
                  >
                    Raw Materials
                  </div>
                  <AlertDescription
                    className="stepInfo"
                    message={productInfoTooltip?.raw_materials_section}
                  />
                  <FormListComp
                    mainName={ProductInfoKey.RAW_MATERIAL_DATA}
                    formComponent={<ProductInfoRaw />}
                  />
                </>
              );
            }}
          </Form.Item>
        </>
      )}
      {currentStep === 1 && (
        <FormListComp
          mainName={ProductInfoKey.FACILITIES}
          formComponent={<ProductFacility />}
        />
      )}
      {currentStep === 2 && (
        <Form.Item dependencies={[ProductInfoKey.PRODUCT_NAME]} noStyle>
          {({getFieldValue: getProductFieldValue}) => {
            const productId = getProductFieldValue(ProductInfoKey.PRODUCT_NAME);

            if (isSacProduct(productId, products)) {
              return null;
            }

            return (
              <MonthlyYearlyDrawerForm
                config={monYear}
                data={getFieldValue(TF) as MYData[]}
              />
            );
          }}
        </Form.Item>
      )}
    </>
  );
};

export default ProductInfoFormDrawerForm;
